/**
 * Phase 101fk.3 (Feb 2026) — File d'attente offline pour le module visite.
 *
 * Objectif : garantir qu'un opérateur en visite ne soit JAMAIS bloqué même
 * sans réseau. Chaque écriture (retrait, comptage, chargement, validate,
 * skip) est mise en file IndexedDB si l'API échoue avec un problème réseau,
 * puis rejouée automatiquement dès le retour du réseau.
 *
 * Design :
 *   - IndexedDB `agromanage-visits` / store `pending_ops`
 *     • id (auto-inc)
 *     • visit_id, slot_number, action, url, method, body
 *     • enqueued_at, tries (0-3), last_error
 *     • idempotency_key (visit_id::slot::action::hash(body)) — pas de doublon
 *   - `visitQueue.enqueue({url, method, body, meta})` retourne { queued: true }
 *   - `visitQueue.drain()` : tente de rejouer les ops en attente (FIFO)
 *   - `visitQueue.count()` : nombre d'ops en attente (pour badge UI)
 *   - Auto-drain au montage du composant, à chaque `online` event, et sur
 *     un intervalle de 20 secondes
 *
 * Erreurs :
 *   - Réseau (fetch throw / status 0 / status >= 500 avec type "network") → enqueue + toast info
 *   - 4xx (validation métier) → NE PAS enqueue, propager l'erreur
 */

import { api } from "@/lib/api";

const DB_NAME = "agromanage-visits";
const STORE = "pending_ops";
const DB_VERSION = 1;
const MAX_TRIES = 5;

let _dbPromise = null;

function _openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB indisponible"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("visit_id", "visit_id", { unique: false });
        store.createIndex("idempotency_key", "idempotency_key", { unique: true });
        store.createIndex("enqueued_at", "enqueued_at", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function _withStore(mode, fn) {
  const db = await _openDb();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
    Promise.resolve(fn(store))
      .then((r) => { result = r; })
      .catch(reject);
  });
}

function _hashBody(body) {
  try {
    const str = JSON.stringify(body || {});
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) - h) + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h).toString(36);
  } catch {
    return Math.random().toString(36).slice(2);
  }
}

function _buildIdempotencyKey(meta, body) {
  const parts = [
    meta.visit_id || "",
    meta.slot_number || "",
    meta.action || "",
    _hashBody(body),
  ];
  return parts.join("::");
}

function _isNetworkError(err) {
  // Axios : err.response absent = réseau ou CORS
  if (!err) return false;
  if (err.code === "ERR_NETWORK") return true;
  if (err.message === "Network Error") return true;
  if (!err.response) return true;
  // 502/503/504 côté ingress → considérer comme réseau (rejouable)
  const s = err.response.status;
  return s === 502 || s === 503 || s === 504;
}

async function _persistOp({ url, method, body, meta }) {
  const now = new Date().toISOString();
  const idempotency_key = _buildIdempotencyKey(meta, body);
  await _withStore("readwrite", (store) => {
    return new Promise((resolve, reject) => {
      // Anti-doublon : check idempotency_key
      const idx = store.index("idempotency_key");
      const check = idx.get(idempotency_key);
      check.onsuccess = () => {
        if (check.result) {
          resolve({ duplicate: true, id: check.result.id });
        } else {
          const addReq = store.add({
            visit_id: meta.visit_id || "",
            slot_number: meta.slot_number || "",
            action: meta.action || "",
            url,
            method,
            body,
            idempotency_key,
            enqueued_at: now,
            tries: 0,
            last_error: "",
          });
          addReq.onsuccess = () => resolve({ duplicate: false, id: addReq.result });
          addReq.onerror = () => reject(addReq.error);
        }
      };
      check.onerror = () => reject(check.error);
    });
  });
}

async function _listOps() {
  return await _withStore("readonly", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

async function _deleteOp(id) {
  await _withStore("readwrite", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  });
}

async function _updateOp(id, patch) {
  await _withStore("readwrite", (store) => {
    return new Promise((resolve, reject) => {
      const g = store.get(id);
      g.onsuccess = () => {
        const rec = g.result;
        if (!rec) { resolve(); return; }
        const merged = { ...rec, ...patch };
        const p = store.put(merged);
        p.onsuccess = () => resolve();
        p.onerror = () => reject(p.error);
      };
      g.onerror = () => reject(g.error);
    });
  });
}

/**
 * Public API : exécute une op en essayant l'API ; en cas de panne réseau,
 * enqueue en IndexedDB pour rejouer plus tard.
 *
 * @param {{url, method, body, meta}} params
 * @returns {Promise<{ok: true, queued?: boolean, data?: any}>}
 */
export async function executeOrEnqueue({ url, method = "POST", body = {}, meta = {} }) {
  try {
    const res = await api.request({ url, method, data: body });
    return { ok: true, queued: false, data: res.data };
  } catch (err) {
    if (_isNetworkError(err)) {
      try {
        const { duplicate } = await _persistOp({ url, method, body, meta });
        return { ok: true, queued: true, duplicate };
      } catch (e) {
        // IndexedDB indispo : propager l'erreur réseau originale
        throw err;
      }
    }
    // 4xx : erreur métier → propager
    throw err;
  }
}

/**
 * Tente de rejouer toutes les ops en attente (FIFO). Idempotent : chaque op
 * a une clé unique côté client ; le backend accepte les rejeux (car chaque
 * écriture ERP est idempotente via `id`).
 *
 * @returns {Promise<{drained: number, failed: number, remaining: number}>}
 */
export async function drainQueue() {
  const ops = await _listOps();
  let drained = 0;
  let failed = 0;
  // FIFO stricte
  ops.sort((a, b) => (a.enqueued_at > b.enqueued_at ? 1 : -1));
  for (const op of ops) {
    if ((op.tries || 0) >= MAX_TRIES) {
      failed += 1;
      continue;
    }
    try {
      await api.request({ url: op.url, method: op.method, data: op.body });
      await _deleteOp(op.id);
      drained += 1;
    } catch (err) {
      if (_isNetworkError(err)) {
        // Toujours offline — stop la boucle
        break;
      }
      // 4xx : le rejeu ne marchera pas — marquer failed et incrémenter tries
      const tries = (op.tries || 0) + 1;
      const msg = err?.response?.data?.detail || err?.message || "erreur inconnue";
      await _updateOp(op.id, { tries, last_error: typeof msg === "string" ? msg.slice(0, 300) : JSON.stringify(msg).slice(0, 300) });
      failed += 1;
    }
  }
  const remaining = (await _listOps()).length;
  return { drained, failed, remaining };
}

/** Nombre d'ops en attente (pour badge UI). */
export async function pendingCount(visitId = null) {
  const ops = await _listOps();
  if (visitId) return ops.filter((o) => o.visit_id === visitId).length;
  return ops.length;
}

/** Liste les ops en attente (drawer debug/audit). */
export async function listPending(visitId = null) {
  const ops = await _listOps();
  if (visitId) return ops.filter((o) => o.visit_id === visitId);
  return ops;
}

/** Purge manuelle (dangereux — pour bouton "réinitialiser"). */
export async function purgeAll() {
  const ops = await _listOps();
  for (const o of ops) await _deleteOp(o.id);
}

// Auto-drain : dès qu'on revient online + toutes les 20 s
let _drainTimer = null;
export function startAutoDrain({ onChange } = {}) {
  const trigger = async () => {
    try {
      const before = await pendingCount();
      if (before === 0) return;
      const res = await drainQueue();
      if (typeof onChange === "function") onChange(res);
    } catch {}
  };
  if (typeof window !== "undefined") {
    window.addEventListener("online", trigger);
    // Petit délai initial pour laisser React se stabiliser
    setTimeout(trigger, 500);
    _drainTimer = setInterval(trigger, 20000);
  }
  return () => {
    if (typeof window !== "undefined") {
      window.removeEventListener("online", trigger);
      if (_drainTimer) clearInterval(_drainTimer);
    }
  };
}
